/**
* @summary - Contract management SubForm Grid
* @author Yuvasri S
* @since 2024-11-29
*/
import { Box, Button, Dialog, DialogActions, DialogContent, DialogTitle, Grid, Stack, Typography } from '@mui/material';
import React, { useEffect, useState, forwardRef, useImperativeHandle } from 'react'
import AddIcon from '@mui/icons-material/Add';
import { useDispatch } from 'react-redux';
import CustomTextFieldWithSearch from '../commoncomponents/CustomTextFieldWithSearch';
import CustomTextField from '../commoncomponents/CustomTextField';
import CustomAgGrid from '../commoncomponents/CustomAgGrid';
import { v4 as uuidv4 } from 'uuid';
import { DefaultCard } from '../../assets/styled-components/card';
import ContractManagementRepository from '../../api/repositories/contractManagementRepository';
import CustomDialog from '../commoncomponents/CustomDialog';
import { useLocation } from 'react-router-dom';
import { dialogContent } from '../../utility/message';

const AddRowDialogue = ({ open, onClose, editMode, editEnable, rows, setRows, service, setError, setRowLength, availableAssets, editDisableAsset, serviceType, headerId, handleSubFormSubmit }) => {
    
    const initialState = {
        assetNo: "",
        assetDesc: "",
        serialNo: "",
        modelNo: "",
        workGroupCode: "",
        workGroupDesc: "",
        PPMRatefor1st: "",
        PPMRatefor2nd: "",
        PPMRatefor3rd: "",
        PPMRatefor4th: "",
        PPMRatefor5th: "",
        totalPPMRate: "",
    };

    const dispatch = useDispatch();
    const [subFormData, setSubFormData] = useState(initialState);
    const [assetNoOptions, setAssetNoOptions] = useState([]);
    const [openConirmDialog, setOpenConfirmDialog] = useState(false)
    const [openInfoDialog, setOpenInfoDialog] = useState(false)
    const [contentDialog, setContentDialog] = useState("")

    const handleConfirm = () => {
        // Clear the error message when save operation begins
        setError("");
        if (editMode?.dummy_id || editMode?.id) {
            if (editMode?.id) {
                // Update existing row
                const updatedRows = rows.map((row) =>
                    row.id === editMode?.id ? subFormData : row
                );
                setRows(updatedRows);
                
            } else if (editMode?.dummy_id) {
                // Update existing row
                const updatedRows = rows.map((row) =>
                    row?.dummy_id === editMode?.dummy_id ? subFormData : row
                );
                setRowLength(rows.length + 1);
                setRows(updatedRows);
                
            }
            setOpenConfirmDialog(false)
        } else {
            const newItem = {
                ...subFormData,
                dummy_id: uuidv4(),
            };

            setRowLength(rows.length + 1);
            setRows((prevRows) => [...prevRows, newItem]);
            // Remove the added asset from assetNoOptions
            setAssetNoOptions((prevOptions) =>
                prevOptions.filter((option) => option.assetId !== subFormData.assetNo.assetId)
            );

            setSubFormData(initialState);
            
            setOpenConfirmDialog(false)


        }
        if (headerId) {
            handleSubFormSubmit(serviceType, headerId, [subFormData])
        }
        onClose();
    }


    const handleOnchange = (value, name) => {
        // Ensure value allows only numeric input with decimals
        if (
            name.startsWith("PPMRatefor") &&
            !/^\d*\.?\d{0,2}$/.test(value) // Allow up to two decimal places
        ) {
            return; // Exit early if the value is invalid
        }

        setSubFormData((prev) => {
            const updateData = {
                ...prev,
                [name]: value
            };
            if (name === "assetNo" && value?.assetId) {

                // updateData.assetNo = {id:value?.assetId, label:value?.assetNo || ''};
                updateData.assetNo = { ...value, label: value?.assetNo ? value?.assetNo : '' };
                updateData.assetDesc = value?.assetDesc || '';
                updateData.workGroupCode = value?.workGroupCode || '';
                updateData.workGroupDesc = value?.workGroupDesc || '';
                updateData.serialNo = value?.serialNo || '';
                updateData.modelNo = value?.modelNo || '';
            } else if (name === "assetNo") {
                updateData.assetNo = "";
                updateData.assetDesc = "";
                updateData.workGroupCode = "";
                updateData.workGroupDesc = "";
                updateData.serialNo = "";
                updateData.modelNo = "";
            }

            const { PPMRatefor1st, PPMRatefor2nd, PPMRatefor3rd, PPMRatefor4th, PPMRatefor5th } = updateData;
            const total = [
                PPMRatefor1st,
                PPMRatefor2nd,
                PPMRatefor3rd,
                PPMRatefor4th,
                PPMRatefor5th,
            ].reduce((acc, curr) => acc + (parseFloat(curr) || 0), 0); // sum up the values

            updateData.totalPPMRate = total.toFixed(2); // set the calculated total

            return updateData;
        });
    };

    const getAssetNo = async () => {
        try {
            const response = await ContractManagementRepository.fetchAssetNo(service);

            // Ensure response is an array or fallback to an empty array
            const assetOptions = Array.isArray(response)
                ? response.map((item) => ({
                    assetId: item?.id || "",
                    label: `${item?.assetNo || ""} - ${item?.assetDesc || ""}` || "",
                    assetDesc: item?.assetDesc || "",
                    assetNo: item?.assetNo || "",
                    workGroupCode: item?.workGroupCode || "",
                    workGroupDesc: item?.workGroupDesc || "",
                    serialNo: item?.serialno || "",
                    modelNo: item?.model || "",
                }))
                : [];


            setAssetNoOptions(assetOptions);
        } catch (error) {
            console.error("Failed to fetch asset numbers:", error);

            // Optionally, you can set the options to an empty array in case of failure
            setAssetNoOptions([]);
        }
    };


    // Sync assetNoOptions with availableAssets
    useEffect(() => {
        setAssetNoOptions((prevOptions) => {
            const mergedOptions = [
                ...prevOptions,
                ...availableAssets
            ];

            // Filter duplicates based on assetId
            const uniqueOptions = mergedOptions.filter(
                (option, index, self) =>
                    index === self.findIndex((t) => t.assetId === option.assetId)
            );

            return uniqueOptions;
        });
    }, [availableAssets]);

    useEffect(() => {
        getAssetNo();
    }, [service]);

    const handleCancel = () => {
        onClose();
        setSubFormData(initialState);
        //   setErrorMessage({});
        //   setError({});
    };

    useEffect(() => {
        if (editMode?.dummy_id || editMode?.id) {
            if (editMode?.dummy_id) {
                setSubFormData({
                    assetNo: { id: editMode?.assetNo?.assetId || "", label: `${editMode?.assetNo?.label}` || "" },
                    assetDesc: editMode?.assetDesc || "",
                    workGroupCode: editMode?.workGroupCode || "",
                    serialNo: editMode?.serialNo || "",
                    modelNo: editMode?.modelNo || "",
                    workGroupDesc: editMode?.workGroupDesc || "",
                    PPMRatefor1st: editMode?.PPMRatefor1st || "",
                    PPMRatefor2nd: editMode?.PPMRatefor2nd || "",
                    PPMRatefor3rd: editMode?.PPMRatefor3rd || "",
                    PPMRatefor4th: editMode?.PPMRatefor4th || "",
                    PPMRatefor5th: editMode?.PPMRatefor5th || "",
                    totalPPMRate: editMode?.totalPPMRate || "",
                    dummy_id: editMode?.dummy_id || ""
                })
            } else if (editMode?.id) {
                setSubFormData({
                    assetNo: { id: editMode?.assetNo?.assetId || "", label: `${editMode?.assetNo?.label}` || "" },
                    assetDesc: editMode?.assetDesc || "",
                    serialNo: editMode?.serialNo || "",
                    modelNo: editMode?.modelNo || "",
                    workGroupCode: editMode?.workGroupCode || "",
                    workGroupDesc: editMode?.workGroupDesc || "",
                    PPMRatefor1st: editMode?.PPMRatefor1st || "",
                    PPMRatefor2nd: editMode?.PPMRatefor2nd || "",
                    PPMRatefor3rd: editMode?.PPMRatefor3rd || "",
                    PPMRatefor4th: editMode?.PPMRatefor4th || "",
                    PPMRatefor5th: editMode?.PPMRatefor5th || "",
                    totalPPMRate: editMode?.totalPPMRate || "",
                    id: editMode?.id || ""
                })
            }

        } else {
            setSubFormData(initialState);
        }
    }, [editMode]);

    const isAddDisabled =
        !subFormData?.assetNo?.label;


    const handleSave = async (e) => {
        e.preventDefault();
        setOpenConfirmDialog(true)
    };

    const handleDialogClose = () => {
        setOpenConfirmDialog(false)
        setOpenInfoDialog(false)
    }

    return (
        <Dialog
            open={open}
            sx={{
                '& .MuiDialog-paper': {
                    width: '50%', // Adjust the width as necessary
                    maxWidth: 'lg', // Set max width to large
                    height: 'auto', // Allow the height to adjust based on content
                    maxHeight: '90vh', // Set a maximum height for the dialog (optional)
                    borderRadius: '10px'
                }
            }}
        >
            <DialogTitle>
                <Typography variant='title2' > {editEnable ? "Edit Asset" : "Add Asset"}</Typography>
            </DialogTitle>
            <DialogContent >
                <Grid container spacing={2}>
                    <Grid item xs={6} md={6}>
                        <CustomTextFieldWithSearch
                            autoFocus={true}
                            label="Asset No"
                            id="assetNo"
                            name="assetNo"
                            value={subFormData?.assetNo || ""}
                            options={assetNoOptions || []}
                            onChange={handleOnchange}
                            isMandatoryField
                            disabled={editDisableAsset}
                        />
                    </Grid>
                    <Grid item xs={6} md={6}>
                        <CustomTextField
                            label="Asset Description"
                            id="assetDesc"
                            name="assetDesc"
                            value={subFormData?.assetDesc || ""}
                            disabled={true}
                        />
                    </Grid>
                    <Grid item xs={6} md={6}>
                        <CustomTextField
                            label="Work Group Code"
                            id="workGroupCode"
                            name="workGroupCode"
                            value={subFormData?.workGroupCode || ""}
                            disabled={true}
                        />
                    </Grid>
                    <Grid item xs={6} md={6}>
                        <CustomTextField
                            label="Work Group Description"
                            id="workGroupDesc"
                            name="workGroupDesc"
                            value={subFormData?.workGroupDesc || ""}
                            disabled={true}
                        />
                    </Grid>
                    <Grid item xs={6} md={6}>
                        <CustomTextField
                            label="Serial No"
                            id="serialNo"
                            name="serialNo"
                            value={subFormData?.serialNo || ""}
                            disabled={true}
                        />
                    </Grid>
                    <Grid item xs={6} md={6}>
                        <CustomTextField
                            label="Model"
                            id="modelNo"
                            name="modelNo"
                            value={subFormData?.modelNo || ""}
                            disabled={true}
                        />
                    </Grid>

                </Grid>
                <Box mt={{ xs: 1, md: 2 }} sx={{ marginTop: '0rem' }}>
                    <Typography variant='title2' >PPM Rate Per Asset</Typography>
                    <Box mb={2} />
                    <Grid container spacing={2}>
                        <Grid item xs={6} md={6}>
                            <CustomTextField
                                label="1st Year"
                                id="PPMRatefor1st"
                                name="PPMRatefor1st"
                                value={subFormData?.PPMRatefor1st || ""}
                                onChange={handleOnchange}
                                onlyNumbers={true}
                                allowDecimal={true}
                                numbersLength={10}
                                textAlign='right'
                            />
                        </Grid>
                        <Grid item xs={6} md={6}>
                            <CustomTextField
                                label="2nd Year"
                                id="PPMRatefor2nd"
                                name="PPMRatefor2nd"
                                value={subFormData?.PPMRatefor2nd || ""}
                                onChange={handleOnchange}
                                onlyNumbers={true}
                                allowDecimal={true}
                                numbersLength={10}
                                textAlign='right'
                            />
                        </Grid>
                        <Grid item xs={6} md={6}>
                            <CustomTextField
                                label="3rd Year"
                                id="PPMRatefor3rd"
                                name="PPMRatefor3rd"
                                value={subFormData?.PPMRatefor3rd || ""}
                                onChange={handleOnchange}
                                onlyNumbers={true}
                                allowDecimal={true}
                                numbersLength={10}
                                textAlign='right'
                            />
                        </Grid>
                        <Grid item xs={6} md={6}>
                            <CustomTextField
                                label="4th Year"
                                id="PPMRatefor4th"
                                name="PPMRatefor4th"
                                value={subFormData?.PPMRatefor4th || ""}
                                onChange={handleOnchange}
                                onlyNumbers={true}
                                allowDecimal={true}
                                numbersLength={10}
                                textAlign='right'
                            />
                        </Grid>
                        <Grid item xs={6} md={6}>
                            <CustomTextField
                                label="5th Year"
                                id="PPMRatefor5th"
                                name="PPMRatefor5th"
                                value={subFormData?.PPMRatefor5th || ""}
                                onChange={handleOnchange}
                                numbersLength={10}
                                textAlign='right'
                                onlyNumbers={true}
                                allowDecimal={true}
                            />
                        </Grid>
                        <Grid item xs={6} md={6}>
                            <CustomTextField
                                label="Total PPM Rate/Asset"
                                id="totalPPMRate"
                                name="totalPPMRate"
                                value={subFormData?.totalPPMRate || ""}
                                disabled={true}
                                textAlign='right'
                            />
                        </Grid>

                    </Grid>
                </Box>
                <CustomDialog
                    confirmBtn={"Ok"}
                    open={openInfoDialog}
                    content={contentDialog}
                    onConfirm={handleDialogClose}
                />
                <CustomDialog
                    open={openConirmDialog}
                    onConfirm={handleConfirm}
                    content={dialogContent.confirmSave}
                    cancelBtn={"No"}
                    confirmBtn={"Yes"}
                    onClose={handleDialogClose}
                />


            </DialogContent>
            <DialogActions>
                <Button variant="contained" onClick={handleSave} disabled={isAddDisabled} >
                    {editEnable ? "UPDATE" : "ADD"}
                </Button>
                <Button variant="outlined" onClick={handleCancel}>
                    CANCEL
                </Button>
            </DialogActions>
        </Dialog>
    );
};


const ContractSubFormGrid = forwardRef((props, ref) => {

    const [gridAPI, setGridAPI] = useState({});
    const [rows, setRows] = useState([]);
    const [dialogOpen, setDialogOpen] = useState(false);
    const [editData, setEditData] = useState({});
    const [editEnable, setEditEnable] = useState(false);
    const [subFormDeleteID, setSubFormDeleteId] = useState({});
    const [subFormDeleteDialog, setSubFormDeleteDialog] = useState(false);
    const location = useLocation();
    const editMode = location?.state?.editmode;
    const renewalMode = location?.state?.isRenewalForm;
    const headerId = location?.state?.parentContractId || location?.state?.id;
    const viewMode = location?.state?.viewMode;
    const serviceType = location?.state?.serviceDesc === 'FEMS' ? 'fem' : 'bem';
    const [error, setError] = useState(""); // State to manage error messages

    const [availableAssets, setAvailableAssets] = useState([]);
    const [editDisableAsset, setEditDisableAsset] = useState(false);

    function AddRowButton({ onClick }) {
        return (
            <Box sx={{ mt: 2 }}>
                <Button color="primary" variant="contained" startIcon={<AddIcon />} onClick={onClick}>
                    Add Row
                </Button>
            </Box>
        );
    };
    const handleOpenDialog = () => {
        setDialogOpen(true);
    };


    const handleCloseDialog = () => {
        setEditData(null);
        setEditEnable(false);
        setEditDisableAsset(false);
        setDialogOpen(false);
    };

    const handleEdit = (rowData) => {
        setEditData(rowData);
        setEditEnable(true);
        setEditDisableAsset(true);
        setDialogOpen(true);
    };

    const handleConfirm = () => {
        handleCloseDialog();
    };


    const getRows = async () => {
        const listRows = await ContractManagementRepository.fetchSubForm(location?.state?.serviceDesc, headerId);     
        const mappedRows = Array.isArray(listRows)
            ? listRows.map(row => ({
                assetNo: {
                    assetId: row?.[`${serviceType}assetregisterId`] || "",                   
                    label: row?.assetno || "",
                },
                assetDesc: row?.assetdesc || "",
                workGroupCode: row?.wgcode || "",
                workGroupDesc: row?.wgdesc || "",
                serialNo: row?.serialno || "",
                modelNo: row?.model || "",
                PPMRatefor1st: row?.ppmrateperasset1 || 0,
                PPMRatefor2nd: row?.ppmrateperasset2 || 0,
                PPMRatefor3rd: row?.ppmrateperasset3 || 0,
                PPMRatefor4th: row?.ppmrateperasset4 || 0,
                PPMRatefor5th: row?.ppmrateperasset5 || 0,
                totalPPMRate: row?.totalppmrateperasset || 0,
                id: row?.id || "",
            }))
            : [];

        setRows(mappedRows);

        // Validation: Check if rows are below the threshold
        if (mappedRows.length < 1) {
            viewMode ? setError("") : setError("Please add at least one row.");
        } else {
            setError(""); // Clear error if valid
        }
    };

    useEffect(() => {
        if(headerId){
            getRows();
        };
       
        
    }, [])

    const columns_Field = [
        {
            field: 'assetNo',
            headerName: 'Asset No',
            flex: 1,
            valueGetter: (params) => params?.data?.assetNo ? params?.data.assetNo?.label : '',
        },
        {
            field: 'assetDesc',
            headerName: 'Asset Description',
            flex: 1
        },
        {
            field: 'workGroupCode',
            headerName: 'Work Group Code',
            flex: 1
        },
        {
            field: 'serialNo',
            headerName: 'Serial No',
            flex: 1
        },
        {
            field: 'modelNo',
            headerName: 'Model',
            flex: 1
        },
        // {
        //     field: 'workGroupDesc',
        //     headerName: 'Work Group Description',
        //     flex: 1
        // },
        {
            field: 'PPMRatefor1st',
            headerName: '1st Year',
            flex: 1,
            cellStyle: { textAlign: 'right' },
            valueGetter: (params) => params?.data?.PPMRatefor1st ? parseFloat(params?.data.PPMRatefor1st || 0) : 0,

        },
        {
            field: 'PPMRatefor2nd',
            headerName: '2nd Year',
            flex: 1,
            cellStyle: { textAlign: 'right' },
            valueGetter: (params) => params?.data?.PPMRatefor2nd ? parseFloat(params?.data.PPMRatefor2nd || 0) : 0,

        },
        {
            field: 'PPMRatefor3rd',
            headerName: '3rd Year',
            flex: 1,
            cellStyle: { textAlign: 'right' },
            valueGetter: (params) => params?.data?.PPMRatefor3rd ? parseFloat(params?.data.PPMRatefor3rd || 0) : 0,

        },
        {
            field: 'PPMRatefor4th',
            headerName: '4th Year',
            flex: 1,
            cellStyle: { textAlign: 'right' },
            valueGetter: (params) => params?.data?.PPMRatefor4th ? parseFloat(params?.data.PPMRatefor4th || 0) : 0,

        },
        {
            field: 'PPMRatefor5th',
            headerName: '5th Year',
            flex: 1,
            cellStyle: { textAlign: 'right' },
            valueGetter: (params) => params?.data?.PPMRatefor5th ? parseFloat(params?.data.PPMRatefor5th || 0) : 0,

        },
        {
            field: 'totalPPMRate',
            headerName: 'Total PPM Rate/Asset',
            flex: 1,
            cellStyle: { textAlign: 'right' },
            valueGetter: (params) => params?.data?.totalPPMRate ? parseFloat(params?.data.totalPPMRate || 0) : 0,

        }
    ];

    const handleDelete = (rowData) => {
        setSubFormDeleteDialog(true);
        setSubFormDeleteId(rowData);
    };

    const handleCloseSubFormDeleteDialog = () => {
        setSubFormDeleteDialog(false);
    };

    const handleConfirmSubFormDelete = async () => {
        try {
            if (subFormDeleteID?.id) {
                await ContractManagementRepository?.deleteSubForm(serviceType, subFormDeleteID?.id);

                const updatedRows = rows?.filter((row) => row?.id !== subFormDeleteID?.id);
                setRows(updatedRows);

                const newAsset = {
                    assetId: subFormDeleteID?.assetNo?.assetId,
                    label: subFormDeleteID?.assetNo?.assetNo
                        ? `${subFormDeleteID?.assetNo?.assetNo} - ${subFormDeleteID?.assetDesc || ""}`
                        : "",
                    assetDesc: subFormDeleteID?.assetDesc,
                    workGroupCode: subFormDeleteID?.assetNo?.workGroupCode,
                    workGroupDesc: subFormDeleteID?.assetNo?.workGroupDesc,
                    assetNo: subFormDeleteID?.assetNo?.assetNo,
                };

                setAvailableAssets([newAsset]);



                setSubFormDeleteDialog(false);

                if (updatedRows.length < 1) {
                    setError("You must have at least one row in the grid.");
                    setSubFormDeleteDialog(false);
                    return;
                }
            } else if (subFormDeleteID?.dummy_id) {
                const updatedRows = rows?.filter((row) => row?.dummy_id !== subFormDeleteID?.dummy_id);
                setRows(updatedRows);

                const newAsset = {
                    assetId: subFormDeleteID?.assetNo?.assetId,
                    label: subFormDeleteID?.assetNo?.assetNo
                        ? `${subFormDeleteID?.assetNo?.assetNo} - ${subFormDeleteID?.assetDesc || ""}`
                        : "",
                    assetDesc: subFormDeleteID?.assetDesc,
                    workGroupCode: subFormDeleteID?.assetNo?.workGroupCode,
                    workGroupDesc: subFormDeleteID?.assetNo?.workGroupDesc,
                    assetNo: subFormDeleteID?.assetNo?.assetNo,
                };


                setAvailableAssets([newAsset]);

                setSubFormDeleteDialog(false);

                if (updatedRows.length < 1) {
                    setError("You must have at least one row in the grid.");
                    setSubFormDeleteDialog(false);
                    return;
                }
            }
        } catch (error) {
            console.error(error);
        }
    };

    useEffect(() => {
        const calculateTotalPPMRate = () => {
            const total = rows.reduce((sum, row) => sum + parseFloat(row.totalPPMRate || 0), 0);
            props.setTotalPPMRate(total); // Pass the total to the parent
        };

        calculateTotalPPMRate();
        
        // if(rows?.length > 0){
        //     console.log(rows,"get rows");
            
        //     setError("");
        // }
         // Validation: Check if rows are below the threshold
         if (rows?.length < 1) {
            viewMode ? setError("") : setError("Please add at least one row.");
        } else {
            setError(""); // Clear error if valid
        }
    }, [rows]); // Run whenever rows change

    const handleValidation = () => {
        if (rows?.length === 0) {
            setError("Please add at least one row before submitting.");
            return false;
        }
    
        if (rows?.length < 1) { // Example threshold (adjust as needed)
            setError("Number of rows must be at least 1.");
            return false;
        }
    
        setError(""); // Clear error if rows are valid
        return true;
    };

    const handleSubFormSubmit = async (serviceType, headerId, updatedRows = []) => {
        const isValid = handleValidation();
        
        const subFormPayload = (updatedRows.length > 0 ? updatedRows : rows)?.map((row) => ({
            ppmrateperasset1: row?.PPMRatefor1st || "",
            ppmrateperasset2: row?.PPMRatefor2nd || "",
            ppmrateperasset3: row?.PPMRatefor3rd || "",
            ppmrateperasset4: row?.PPMRatefor4th || "",
            ppmrateperasset5: row?.PPMRatefor5th || "",
            totalppmrateperasset: row?.totalPPMRate || "",
            [`${serviceType}contractregisterheaderId`]: headerId,
            [`${serviceType}assetregisterId`]: row?.assetNo?.assetId || row?.assetNo?.id || "",
            id: editMode ? row?.id : ""
        }));

        console.log(isValid, "isValid");
        if (isValid) {
            try {
                const createSubFormRes = editMode
                    ? await ContractManagementRepository.updateSubForm(serviceType, headerId, subFormPayload)
                    : await ContractManagementRepository.createSubForm(serviceType, subFormPayload);
                if (editMode) {
                    getRows();
                }
                return createSubFormRes;
            } catch (error) {
                console.error('Error while submitting subform:', error);
            }
        }

    };

     // Expose handleSubFormSubmit to the parent via ref
     useImperativeHandle(ref, () => ({
        handleSubFormSubmit,
        handleValidation
    }));
    
console.log(rows,"rows");

    return (
        <>
            <DefaultCard>
                <Box mt={{ xs: 1, md: 2 }} sx={{ marginTop: '0rem' }}>
                   
                        <Stack direction="row" spacing={1.5} alignItems="center" justifyContent="space-between" mb={2}>
                            <Typography variant='title2'> Asset List and Cost Details</Typography>
                            <Box sx={{ flexGrow: 1 }} />
                            {!viewMode && ( <AddRowButton onClick={handleOpenDialog} /> )}
                        </Stack>
                    {error && (
                        <Typography component={"span"} color={"error"}>
                            {error}*
                        </Typography>
                    )}

                    <CustomAgGrid
                        refSet={gridAPI}
                        rows={rows}
                        columns={columns_Field}
                        setGridApi={setGridAPI}
                        handleEdit={!viewMode ? (rowData) => handleEdit(rowData) : null}
                        handleDelete={!viewMode ? (rowData) => handleDelete(rowData) : null}
                    />


                </Box>

                <AddRowDialogue
                    service={editMode ? location?.state?.serviceDesc : props.service}
                    open={dialogOpen}
                    onClose={handleCloseDialog}
                    onConfirm={handleConfirm}
                    rows={rows}
                    setRows={setRows}
                    editMode={editData}
                    editEnable={editEnable}
                    setError={setError}
                    setRowLength={props.setRowLength}
                    availableAssets={availableAssets} // Pass availableAssets to AddRowDialogue
                    editDisableAsset={editDisableAsset}
                    serviceType={serviceType}
                    headerId={headerId}
                    handleSubFormSubmit={handleSubFormSubmit}
                />

                <CustomDialog
                    open={subFormDeleteDialog}
                    onClose={handleCloseSubFormDeleteDialog}
                    onConfirm={handleConfirmSubFormDelete}
                    content="Are you sure you want to Delete?"
                    cancelBtn="No"
                    confirmBtn="Yes"
                />

            </DefaultCard>
        </>
    )
})

export default ContractSubFormGrid;